import { DateTimeSDK } from '@funkey/shared';

/** Displays the given `date` in the given `format` with the `Europe/Brussels` timezone.
 *
 * # Why?
 * - `new Date().toString()` is wayy too verbose
 * - `new Date().toLocaleString()` renders based on the timezone of the system,
 *   which isn't the same on the browser (Europe/Brussels) and server (UTC) */
export const DateTime: React.FC<{
    format: 'date' | 'time' | 'date+time';
    /** Either a single date or a date range */
    date:
    // Single date
    | Date
    // Date range
    | { from: Date; to: Date; }; // prettier-ignore
}> = props => {
    if (!props.date) return null;

    if (props.date instanceof Date) {
        if (!DateTimeSDK.isValid(props.date)) return null;

        // Single date
        switch (props.format) {
            case 'date':
                return DateTimeSDK.toLocaleDateStr(props.date);
            case 'time':
                return DateTimeSDK.toLocaleTimeStr(props.date);
            case 'date+time':
                return `${DateTimeSDK.toLocaleDateStr(props.date)} ${DateTimeSDK.toLocaleTimeStr(props.date)}`;
            default:
                props.format satisfies never;
        }
    } else {
        if (
            !props.date.from ||
            !props.date.to ||
            !DateTimeSDK.isValid(props.date.from) ||
            !DateTimeSDK.isValid(props.date.to)
        ) {
            return null;
        }

        // Date range
        switch (props.format) {
            case 'date': {
                const fromDate = DateTimeSDK.toLocaleDateStr(props.date.from);
                const toDate = DateTimeSDK.toLocaleDateStr(props.date.to);

                return fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`;
            }
            case 'time': {
                return `${DateTimeSDK.toLocaleTimeStr(props.date.from)} - ${DateTimeSDK.toLocaleTimeStr(props.date.to)}`;
            }
            case 'date+time': {
                const fromDate = DateTimeSDK.toLocaleDateStr(props.date.from);
                const toDate = DateTimeSDK.toLocaleDateStr(props.date.to);

                const fromTime = DateTimeSDK.toLocaleTimeStr(props.date.from);
                const toTime = DateTimeSDK.toLocaleTimeStr(props.date.to);

                return fromDate === toDate
                    ? `${fromDate} ${fromTime} - ${toTime}`
                    : `${fromDate} ${fromTime} - ${toDate} ${toTime}`;
            }

            default:
                props.format satisfies never;
        }
    }
};
